import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Descriptions, Divider, Drawer, Flex, Form, Grid, Popconfirm, Skeleton, Space, Table, Typography } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import type { ColumnsType } from 'antd/es/table';
import { type Dispatch, Fragment, type SetStateAction, useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { confirmBusinessTripTicket, getBusinessTripTicket } from 'src/apis/hr_ticket/businessTripTicket.api';
import { BtnCustom } from 'src/components/v2/Button';
import { HRTicketStatusTag } from 'src/components/v2/Tag/TicketStatusTag/TicketStatusTag';
import { ConfirmTypeEn } from 'src/constants/confirmTypeEn.enum';
import urlPath from 'src/constants/urlPath';
import { AppContext } from 'src/contexts/app.context';
import { TicketDetailContext } from 'src/contexts/ticketDetail.context';
import useCalcTimeBusiness from 'src/hooks/useCalcTimeBusiness';
import AddRejectNote from 'src/pages/v2/HrTicket/components/AddRejectNote';
import AddConfirmBusinessTripTicket from 'src/pages/v2/HrTicket/ConfirmBusinessTripTicket/AddConfirmBusinessTripTicket';
import { TicketStatus } from 'src/types/helpdesk/ticket.type';
import type {
  BusinessTripTicketConfirm,
  BusinessTripTicketDetailConfirmed,
  IBusinessTripTicketDetail
} from 'src/types/hr_ticket/businessTripTicket.type';
import { showNotification } from 'src/utils/notification';
import { formatDate, formatTimeString, totalOfRangeDate } from 'src/utils/utils';
import type { OpenDrawerType } from '../../TicketRelative/BusinessTripTicket';
import '../TicketRelativeDrawer.scss';

const { Text, Title } = Typography;

type Props = {
  open: OpenDrawerType;
  setOpen: Dispatch<SetStateAction<OpenDrawerType>>;
};

export type OpenAddNoteType = {
  id?: number;
  isOpen: boolean;
};

const columns = function (
  isConfirm: ConfirmTypeEn | undefined,
  calcTotalTime: (
    startTimeInput: string | undefined,
    endTimeInput: string | undefined,
    employeeId: number | undefined
  ) => number
): ColumnsType<Partial<IBusinessTripTicketDetail>> {
  const columnTitle: ColumnsType<Partial<IBusinessTripTicketDetail>> = [
    {
      title: 'Người đăng ký',
      dataIndex: 'employee',
      key: 'employee',
      width: '18%',
      render: (_, record) => (
        <Text strong>
          {record.employee?.code} - {record.employee?.fullName}
        </Text>
      )
    },
    {
      title: 'Ngày công tác',
      dataIndex: 'date',
      key: 'date',
      width: '20%',
      align: 'center',
      render: (_, record) => {
        return (
          <Flex vertical gap={5}>
            <Text strong>
              {formatDate(record?.fromDate)} - {formatDate(record?.toDate)}
            </Text>
            <Text>Tổng: {totalOfRangeDate(record?.fromDate, record?.toDate)} Ngày</Text>
          </Flex>
        );
      }
    },
    {
      title: 'Thời gian',
      dataIndex: '',
      key: 'time',
      width: '12%',
      align: 'center',
      render: (_, record) => (
        <Flex vertical gap={5}>
          <Text strong>
            {formatTimeString(record?.startTime, true)} - {formatTimeString(record?.endTime)}
          </Text>
          <Text>Tổng: {calcTotalTime(record?.startTime, record?.endTime, record.employeeId)} Tiếng</Text>
        </Flex>
      )
    },
    {
      title: 'Số ngày lưu trú',
      dataIndex: '',
      key: 'time',
      width: '12%',
      align: 'center',
      render: (_, record) => <Text>{record.numDaysOfStay} Ngày</Text>
    },
    {
      title: 'Số ngày công tác phí',
      dataIndex: '',
      key: 'time',
      width: '18%',
      align: 'center',
      render: (_, record) => <Text>{record.numDaysOfBusinessTrip} Ngày</Text>
    }
  ];

  return columnTitle;
};

type FormStateType = {
  note: string;
  confirms: BusinessTripTicketDetailConfirmed[];
};

const initFormState: FormStateType = {
  confirms: [],
  note: ''
};

function BusinessTripTicketDrawer({ open, setOpen }: Props) {
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const [formState, setFormState] = useState({ ...initFormState });
  const [openNote, setOpenNote] = useState(false);
  const { ticket } = useContext(TicketDetailContext);
  const { userInfo } = useContext(AppContext);
  const { calcTotalTime } = useCalcTimeBusiness();

  const { useBreakpoint } = Grid;
  const breakpoints = useBreakpoint();
  const colspan = breakpoints.xxl ? 2 : 1;

  const businessTripTicketQuery = useQuery({
    queryKey: ['businessTripTicket', open.id],
    queryFn: () => getBusinessTripTicket(Number(open.id)),
    enabled: !!open.id
  });

  useEffect(() => {
    if (businessTripTicketQuery.data) {
      const confirms = [...businessTripTicketQuery.data.data.businessTripTicketDetails].map((item) => {
        return { ...item, actualFromDate: item.fromDate, actualToDate: item.toDate };
      });

      setFormState((prev) => ({ ...prev, confirms: confirms }));
    }
  }, [businessTripTicketQuery.data]);

  const confirmBusinessTripMutation = useMutation({
    mutationFn: (businessTripTicketDetail: BusinessTripTicketConfirm) =>
      confirmBusinessTripTicket(Number(open.id), businessTripTicketDetail),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['businessTripTicket', open.id] });
      showNotification('Xác nhận phiếu thành công', 'success');
    }
  });

  const handleConfirm = () => {
    form
      .validateFields()
      .then(() => {
        const newConfirms = formState.confirms.filter((x) => !x.isUnConfirm);
        if (newConfirms.length <= 0) {
          showNotification('Phải có ít nhất 1 người, để hoàn thành xác nhận!');
          return;
        } else {
          const newOvertimeTicketConfirms = formState.confirms
            .filter(({ isUnConfirm }) => !isUnConfirm)
            ?.map((item) => {
              return {
                refDetailId: item.refDetailId ? item.refDetailId : item.id,
                employeeId: item?.employeeId,
                fromDate: item?.fromDate,
                toDate: item?.toDate,
                startTime: item?.startTime,
                endTime: item?.endTime,
                numDaysOfStay: item?.numDaysOfStay,
                numDaysOfBusinessTrip: item?.numDaysOfBusinessTrip,
                businessTripTicketId: item?.businessTripTicketId
              };
            });

          confirmBusinessTripMutation.mutate({
            confirmNote: formState.note,
            confirmType: ConfirmTypeEn.Confirmed,
            confirms: newOvertimeTicketConfirms
          });
        }
      })
      .catch(() => {
        showNotification('Vui lòng nhập kết quả công việc!', 'warning');
      });
  };

  const handleSetFormState = (businessTripTicketDetails: BusinessTripTicketDetailConfirmed[]) => {
    setFormState({ ...formState, confirms: businessTripTicketDetails });
  };

  const handleReject = (note: string) => {
    confirmBusinessTripMutation.mutate({
      confirmNote: note,
      confirmType: ConfirmTypeEn.Reject,
      confirms: []
    });
  };

  return (
    <Drawer
      title={
        <Flex justify='space-between' align='center'>
          <Text>Thông tin công tác</Text>
          {ticket?.data.createdByObj.id === userInfo?.id &&
            ticket?.data.ticketStatusId !== TicketStatus.New &&
            businessTripTicketQuery.data?.data.ticketStatusId === TicketStatus.ApprovedRequest &&
            businessTripTicketQuery.data?.data.isConfirm === ConfirmTypeEn.WaitingConfirm && (
              <Space>
                <Popconfirm title={'Bạn chắc chắn muốn xác nhận?'} onConfirm={handleConfirm}>
                  <BtnCustom
                    type='confirm'
                    size='small'
                    isLoading={
                      confirmBusinessTripMutation.variables?.confirmType === ConfirmTypeEn.Confirmed &&
                      confirmBusinessTripMutation.isLoading
                    }
                  />
                </Popconfirm>
                <BtnCustom
                  type='noConfirm'
                  size='small'
                  onClick={() => setOpenNote(true)}
                  isLoading={
                    confirmBusinessTripMutation.variables?.confirmType === ConfirmTypeEn.Reject &&
                    confirmBusinessTripMutation.isLoading
                  }
                />
              </Space>
            )}
        </Flex>
      }
      rootClassName='overtime-ticket-drawer'
      styles={{ header: { borderBlockEnd: '1px solid #f0f0f0' } }}
      open={open.isOpen}
      onClose={() => setOpen({ isOpen: false })}
      width={'70%'}
      mask={true}
    >
      <Fragment>
        <Skeleton loading={businessTripTicketQuery.isFetching} active>
          <Fragment>
            <Flex justify='start'>
              <Title level={5}>Thông tin phiếu</Title>
            </Flex>
            <Fragment>
              <Descriptions
                className='business-trip-item-header'
                layout='horizontal'
                labelStyle={{ width: '135px' }}
                column={{ xs: 1, sm: 1, md: 1, lg: 1, xl: 1, xxl: 2 }}
              >
                <Descriptions.Item label='Mã phiếu'>
                  <Text strong>{businessTripTicketQuery.data?.data?.code}</Text>
                </Descriptions.Item>
                <Descriptions.Item label='Trạng thái'>
                  <HRTicketStatusTag
                    ticketStatus={businessTripTicketQuery.data?.data.ticketStatus}
                    erpKey={businessTripTicketQuery.data?.data.erpKey}
                    isConfirm={businessTripTicketQuery.data?.data.isConfirm}
                    hrConfirmedType={businessTripTicketQuery.data?.data.hrConfirmedType}
                  />
                </Descriptions.Item>
                <Descriptions.Item label='Ngày tạo' span={colspan}>
                  <Text>{formatDate(businessTripTicketQuery.data?.data?.createdDate)}</Text>
                </Descriptions.Item>
                <Descriptions.Item label='Người tạo'>
                  <Text>{businessTripTicketQuery.data?.data?.createdBy?.fullName}</Text>
                </Descriptions.Item>
                <Descriptions.Item label='Bộ phận'>
                  <Text>{businessTripTicketQuery.data?.data?.createdBy?.department?.name}</Text>
                </Descriptions.Item>
                <Descriptions.Item label='Ticket raise' span={colspan}>
                  <Text strong>
                    <Link to={`${urlPath.myTicket.location}/${businessTripTicketQuery.data?.data?.refTicketId}`}>
                      {businessTripTicketQuery.data?.data?.refTicketCode}
                    </Link>
                  </Text>
                </Descriptions.Item>
                <Descriptions.Item label='Mã dự án'>
                  {businessTripTicketQuery.data?.data?.projectCode}
                </Descriptions.Item>
                <Descriptions.Item label='Số phiếu ERP'>{businessTripTicketQuery.data?.data?.erpKey}</Descriptions.Item>
                <Descriptions.Item label='Nơi đi' span={colspan}>
                  {(businessTripTicketQuery.data?.data?.departureAddress &&
                    businessTripTicketQuery.data?.data?.departureAddress + ', ') ||
                    ''}
                  {businessTripTicketQuery.data?.data?.departureIdInString || ''}
                </Descriptions.Item>
                <Descriptions.Item label='Nơi đến' span={colspan}>
                  {businessTripTicketQuery.data?.data?.destinationAddress &&
                    businessTripTicketQuery.data?.data?.destinationAddress + ', '}
                  {businessTripTicketQuery.data?.data?.destinationIdInString}
                </Descriptions.Item>
                <Descriptions.Item label={'Chi tiết công việc'} span={colspan}>
                  <Text>{businessTripTicketQuery.data?.data?.note || ''}</Text>
                </Descriptions.Item>
                <Descriptions.Item label={'Kết quả công việc'} span={colspan}>
                  <Text strong>{businessTripTicketQuery.data?.data?.confirmNote || ''}</Text>
                </Descriptions.Item>
              </Descriptions>
              <Divider />
              <Flex vertical gap={16}>
                <Flex vertical>
                  <Flex justify='space-between' align='center' className='overtime-detail-list-heading'>
                    <Title level={5} style={{ marginBottom: '0px' }}>
                      Danh sách đăng ký
                    </Title>
                  </Flex>
                  <AddConfirmBusinessTripTicket
                    isConfirm={!!businessTripTicketQuery.data?.data.isConfirm}
                    confirms={formState.confirms}
                    handleSetFormState={handleSetFormState}
                  />
                </Flex>
                {businessTripTicketQuery.data?.data.isConfirm === ConfirmTypeEn.WaitingConfirm && (
                  <Form
                    className='business-trip-confirm-form'
                    layout='vertical'
                    form={form}
                    labelCol={{ span: 7 }}
                    fields={[
                      {
                        name: 'note',
                        value: formState.note
                      }
                    ]}
                  >
                    <Form.Item
                      label={<Text strong>Kết quả công việc</Text>}
                      name='note'
                      rules={[{ required: true, message: '' }]}
                    >
                      <TextArea
                        onChange={(e) => setFormState((prev) => ({ ...prev, note: e.target.value }))}
                        rows={4}
                        placeholder='Nhập kết quả công việc'
                      />
                    </Form.Item>
                  </Form>
                )}
              </Flex>
              {businessTripTicketQuery.data?.data.isConfirm === ConfirmTypeEn.Confirmed && <Divider />}
              {businessTripTicketQuery.data?.data.isConfirm === ConfirmTypeEn.Confirmed && (
                <Flex vertical>
                  <Flex justify='space-between' align='center' className='overtime-detail-list-heading'>
                    <Title level={5} style={{ marginBottom: '0px' }}>
                      Danh sách xác nhận
                    </Title>
                  </Flex>
                  <Table
                    className='business-trip-confirm-table'
                    rowClassName={'confirmed'}
                    columns={columns(businessTripTicketQuery.data?.data.isConfirm, calcTotalTime)}
                    rowKey={(record) => String(record.id)}
                    dataSource={businessTripTicketQuery.data?.data.confirms}
                    pagination={false}
                    bordered={false}
                    size='middle'
                  />
                </Flex>
              )}
            </Fragment>
          </Fragment>
        </Skeleton>
      </Fragment>

      <AddRejectNote open={openNote} setOpen={setOpenNote} handleAddNote={handleReject} />
    </Drawer>
  );
}

export default BusinessTripTicketDrawer;
